import { loc, type Lang, type Loc } from './cards.ts'
/**
 * Места рождения. Координаты нужны для Асцендента, часовой пояс — чтобы
 * перевести местное время рождения в UTC (исторические переводы часов берутся
 * из ICU по идентификатору зоны, см. astro.ts).
 *
 * Поиск идёт через геокодер (см. server/src/geocode.ts). Список ниже —
 * подсказки для целевого рынка и запасной вариант, когда геокодер недоступен:
 * без него приложение осталось бы вообще без выбора города.
 */

export interface Place {
  /** Локализованное название города. */
  name: string
  /** Страна или регион — чтобы различать тёзок. */
  region?: string
  latitude: number
  longitude: number
  /** Идентификатор зоны IANA, например Asia/Baku. */
  timeZone: string
}

interface PopularPlace {
  name: Loc
  /** Страна или регион — чтобы различать тёзок. */
  region: Loc
  latitude: number
  longitude: number
  timeZone: string
}

/**
 * Страны вынесены отдельно: одна и та же повторяется у шести городов, и
 * разъехаться четырём написаниям в шести местах ничего не мешало бы.
 */
const AZERBAIJAN: Loc = { ru: 'Азербайджан', az: 'Azərbaycan', en: 'Azerbaijan', tr: 'Azerbaycan' }
const GEORGIA: Loc =    { ru: 'Грузия',      az: 'Gürcüstan',  en: 'Georgia',    tr: 'Gürcistan' }
const TURKEY: Loc =     { ru: 'Турция',      az: 'Türkiyə',    en: 'Türkiye',    tr: 'Türkiye' }
const RUSSIA: Loc =     { ru: 'Россия',      az: 'Rusiya',     en: 'Russia',     tr: 'Rusya' }
const UKRAINE: Loc =    { ru: 'Украина',     az: 'Ukrayna',    en: 'Ukraine',    tr: 'Ukrayna' }
const KAZAKHSTAN: Loc = { ru: 'Казахстан',   az: 'Qazaxıstan', en: 'Kazakhstan', tr: 'Kazakistan' }
const UZBEKISTAN: Loc = { ru: 'Узбекистан',  az: 'Özbəkistan', en: 'Uzbekistan', tr: 'Özbekistan' }
const UAE: Loc =        { ru: 'ОАЭ',         az: 'BƏƏ',        en: 'UAE',        tr: 'BAE' }

/**
 * Слова, по которым страну ищут, но которыми её не подписывают.
 *
 * Подпись и поисковый запрос — разные вещи: по-английски мы пишем `Türkiye`
 * (так же называет её геокодер: «Republic of Türkiye»), а набирают обычно
 * `Turkey`. Отказывать на этом слове незачем — принимаем оба, показываем одно.
 * Ключ — английская подпись, она у страны единственная и не меняется.
 */
const REGION_ALIASES: Record<string, string> = {
  Türkiye: 'Turkey',
  UAE: 'Emirates United Arab Emirates Birleşik Arap Emirlikleri Объединённые Арабские Эмираты',
}

/**
 * Названия — на всех четырёх языках, а не на двух.
 *
 * Раньше их было два, кириллица и латиница, и латиница отдавалась заодно
 * турецкому с английским. Письменность у трёх языков общая, а орфография — нет:
 * турок видел `Bakı (Azərbaycan)` вместо `Bakü (Azerbaycan)`, англичанин — то
 * же самое вместо `Baku (Azerbaijan)`. Заметнее всего Тбилиси: по-турецки он
 * `Tiflis`, и по «Tbilisi» человек его не узнаёт.
 *
 * Написания сверены по межъязыковым ссылкам Википедии, а не выведены на слух:
 * `Sumgayıt`, `Nahçıvan`, `Lenkeran` угадать нельзя.
 */
const POPULAR: PopularPlace[] = [
  { name: { ru: 'Баку',            az: 'Bakı',            en: 'Baku',             tr: 'Bakü' },            region: AZERBAIJAN, latitude: 40.4093, longitude: 49.8671, timeZone: 'Asia/Baku' },
  { name: { ru: 'Гянджа',          az: 'Gəncə',           en: 'Ganja',            tr: 'Gence' },           region: AZERBAIJAN, latitude: 40.6828, longitude: 46.3606, timeZone: 'Asia/Baku' },
  { name: { ru: 'Сумгаит',         az: 'Sumqayıt',        en: 'Sumgait',          tr: 'Sumgayıt' },        region: AZERBAIJAN, latitude: 40.5892, longitude: 49.6686, timeZone: 'Asia/Baku' },
  { name: { ru: 'Нахичевань',      az: 'Naxçıvan',        en: 'Nakhchivan',       tr: 'Nahçıvan' },        region: AZERBAIJAN, latitude: 39.2090, longitude: 45.4122, timeZone: 'Asia/Baku' },
  { name: { ru: 'Шеки',            az: 'Şəki',            en: 'Shaki',            tr: 'Şeki' },            region: AZERBAIJAN, latitude: 41.1919, longitude: 47.1706, timeZone: 'Asia/Baku' },
  { name: { ru: 'Ленкорань',       az: 'Lənkəran',        en: 'Lankaran',         tr: 'Lenkeran' },        region: AZERBAIJAN, latitude: 38.7529, longitude: 48.8475, timeZone: 'Asia/Baku' },
  { name: { ru: 'Тбилиси',         az: 'Tbilisi',         en: 'Tbilisi',          tr: 'Tiflis' },          region: GEORGIA,    latitude: 41.7151, longitude: 44.8271, timeZone: 'Asia/Tbilisi' },
  { name: { ru: 'Стамбул',         az: 'İstanbul',        en: 'Istanbul',         tr: 'İstanbul' },        region: TURKEY,     latitude: 41.0082, longitude: 28.9784, timeZone: 'Europe/Istanbul' },
  { name: { ru: 'Москва',          az: 'Moskva',          en: 'Moscow',           tr: 'Moskova' },         region: RUSSIA,     latitude: 55.7558, longitude: 37.6173, timeZone: 'Europe/Moscow' },
  { name: { ru: 'Санкт-Петербург', az: 'Sankt-Peterburq', en: 'Saint Petersburg', tr: 'Sankt-Peterburg' }, region: RUSSIA,     latitude: 59.9311, longitude: 30.3609, timeZone: 'Europe/Moscow' },
  { name: { ru: 'Киев',            az: 'Kiyev',           en: 'Kyiv',             tr: 'Kiev' },            region: UKRAINE,    latitude: 50.4501, longitude: 30.5234, timeZone: 'Europe/Kyiv' },
  { name: { ru: 'Алматы',          az: 'Almatı',          en: 'Almaty',           tr: 'Almatı' },          region: KAZAKHSTAN, latitude: 43.2220, longitude: 76.8512, timeZone: 'Asia/Almaty' },
  { name: { ru: 'Ташкент',         az: 'Daşkənd',         en: 'Tashkent',         tr: 'Taşkent' },         region: UZBEKISTAN, latitude: 41.2995, longitude: 69.2401, timeZone: 'Asia/Tashkent' },
  { name: { ru: 'Дубай',           az: 'Dubay',           en: 'Dubai',            tr: 'Dubai' },           region: UAE,        latitude: 25.2048, longitude: 55.2708, timeZone: 'Asia/Dubai' },
]

const shown = (p: PopularPlace, lang: Lang): Place => ({
  name: loc(p.name, lang),
  region: loc(p.region, lang),
  latitude: p.latitude,
  longitude: p.longitude,
  timeZone: p.timeZone,
})

export function popularPlaces(lang: Lang): Place[] {
  return POPULAR.map((p) => shown(p, lang))
}

/**
 * Написание без диакритики и в нижнем регистре — для сравнения, а не для показа.
 *
 * На телефоне город набирают как придётся: «Seki» вместо «Şəki», «Taskent»
 * вместо «Taşkent», «Baki» вместо «Bakı». Без приведения такой запрос не
 * находил ничего — а это самый обычный способ набора, когда раскладка чужая.
 *
 * Разложение NFD снимает то, что раскладывается (ş, ğ, ç, ö, ü), остальное
 * дописываем руками: `ə` и `ı` — самостоятельные буквы, и в NFD они не
 * распадаются. Приведение регистра идёт ДО них, иначе турецкая `İ` оставляет
 * после себя отдельную точку (U+0307) и «İstanbul» не совпадает с «istanbul».
 */
const fold = (s: string): string =>
  s.normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/ə/g, 'e')
    .replace(/ı/g, 'i')

/** Поиск по локальному списку — мгновенные подсказки и запасной вариант. */
export function searchPopular(query: string, lang: Lang): Place[] {
  const q = fold(query.trim())
  if (!q) return popularPlaces(lang)
  // Ищем по ВСЕМ написаниям, а не только по языку интерфейса: человек набирает
  // город так, как привык, а привычка не следует за выбранным языком. Турок с
  // русским интерфейсом набирает «Tiflis», азербайджанец — «Bakı».
  const hitName = (p: PopularPlace) => fold(Object.values(p.name).join(' ')).includes(q)
  // Страна ищется тоже: она подписана рядом с городом, и набрать увиденное
  // человек не мог — «Azerbaijan» не находил ни одного из шести наших
  // азербайджанских городов.
  const hitRegion = (p: PopularPlace) =>
    fold([...Object.values(p.region), REGION_ALIASES[p.region.en] ?? ''].join(' ')).includes(q)
  // Совпадения по имени идут первыми: на «ge» человек ищет Gence, а не грузинский
  // Тбилиси, хотя формально подходят оба.
  return [...POPULAR.filter(hitName), ...POPULAR.filter((p) => !hitName(p) && hitRegion(p))]
    .map((p) => shown(p, lang))
}

export const DEFAULT_PLACE: Place = {
  name: 'Баку',
  region: 'Азербайджан',
  latitude: 40.4093,
  longitude: 49.8671,
  timeZone: 'Asia/Baku',
}

/** Координаты и зона в допустимых пределах — данные приходят от клиента. */
export function isValidPlace(p: unknown): p is Place {
  if (typeof p !== 'object' || p === null) return false
  const q = p as Partial<Place>
  if (typeof q.name !== 'string' || q.name.length === 0 || q.name.length > 120) return false
  if (typeof q.latitude !== 'number' || !Number.isFinite(q.latitude) || Math.abs(q.latitude) > 90) return false
  if (typeof q.longitude !== 'number' || !Number.isFinite(q.longitude) || Math.abs(q.longitude) > 180) return false
  if (typeof q.timeZone !== 'string') return false
  try {
    // Единственный надёжный способ проверить зону — попросить ICU её разобрать.
    new Intl.DateTimeFormat('en-US', { timeZone: q.timeZone })
  } catch {
    return false
  }
  return true
}
